import { MouseEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Description, Field, Input, Label } from "@headlessui/react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { isString } from "../../app/utils";
import {
	clearCourse,
	nullAccessibility,
	setAccessibility,
	setDistrict,
	setIsLoggedIn,
	setSchool,
	setUser,
} from "../../app/userSlice";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import Password from "../../components/input/Password";
import RequiredField from "../../components/RequiredField";
import { SelectList } from "../../components/input/SelectList";
import { applyAccessibility } from "../accessibility/accessibilityUtil";
import { useGetSelfMutation } from "../users/usersApiSlice";
import {
	ProctorRegistrationUserEditModel,
	useGetRegisterProctorInfoQuery,
	useRegisterProctorMutation,
} from "./registrationApiSlice";

export default function RegisterProctor() {
	const { config } = useAppSelector((state) => state.site);
	const { userId: loggedInUserId } = useAppSelector((state) => state.user);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const result = useGetRegisterProctorInfoQuery();
	const registrationInfo = result.data;

	const [registerProctor] = useRegisterProctorMutation();
	const [callGetSelf] = useGetSelfMutation();

	const [registrationError, setRegistrationError] = useState<string>();
	const [disableSave, setDisableSave] = useState(true);
	const [externalId, setExternalId] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState<string>();
	const [hash, setHash] = useState("");
	const [birthMonth, setBirthMonth] = useState("01");
	const [birthDate, setBirthDate] = useState("01");
	const [gender, setGender] = useState<number>(0);

	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");

	useEffect(() => {
		if (
			!externalId ||
			!firstName ||
			!lastName ||
			!email ||
			!hash ||
			!username ||
			!password ||
			!confirmPassword
		) {
			setDisableSave(true);
		} else {
			setDisableSave(false);
		}
	}, [externalId, firstName, lastName, email, hash, username, password, confirmPassword]);

	function handleSave(e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) {
		e.preventDefault();
		const model = GetUpdateModel();

		if (model) {
			registerProctor(model)
				.unwrap()
				.then(() => {
					handleSuccessfulLogin();
				})
				.catch((error) => {
					console.log(error);
					setRegistrationError(error.data);
					scrollTo(0, 0);
				});
		}
	}

	const handleSuccessfulLogin = () => {
		callGetSelf()
			.unwrap()
			.then((payload) => {
				if (payload.userId !== loggedInUserId) {
					dispatch(setUser({ ...payload }));
					dispatch(
						setAccessibility(
							payload.accessibility
								? { ...payload.accessibility }
								: { ...nullAccessibility }
						)
					);
					dispatch(clearCourse());
					dispatch(setSchool(payload.school));
					dispatch(setDistrict(payload.district));
					applyAccessibility(payload.accessibility);
				}
				dispatch(setIsLoggedIn(true));
				!payload.requiresAccountConfirmation && !payload.isImpersonating
					? navigate("/myAccount")
					: navigate("/courses");
			})
			.catch((error: any) => {
				const e = isString(error) ? error : JSON.stringify(error);
				console.log(`error calling GetSelf, error = ${e}`);
			});
	};

	function GetUpdateModel(): ProctorRegistrationUserEditModel | null {
		if (!registrationInfo) return null;
		const model: ProctorRegistrationUserEditModel = {
			...registrationInfo,
			fName: firstName,
			lName: lastName,
			emailAddress: email,
			phone: phone,
			externalID: externalId,
			hash: hash,
			birthmonth: birthMonth,
			birthday: birthDate,
			gender: gender,
			username: username,
			password: password,
			confirmPassword: confirmPassword,
		};
		return model;
	}

	const handleGenderChange = (gender: string) => {
		setGender(Number(gender));
	};

	const handleBirthMonthChange = (month: string) => {
		setBirthMonth(month);
	};

	const handleBirthDateChange = (day: string) => {
		setBirthDate(day);
	};

	return (
		<form>
			<div className="space-y-6 mt-8">
				<div>
					<h1 className="text-3xl">Teacher Registration</h1>
					<p className="mt-2">
						Enter your information in the fields below to create a new teacher account.
					</p>
				</div>
				<AlertMessage
					className="mt-8"
					type={AlertLevel.Error}
					message={registrationError}
					showIcon={true}
					onClear={() => setRegistrationError(undefined)}
				/>
				<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
					<div>
						<h2 className="text-2xl leading-7">Teacher Information</h2>
						<p className="mt-1 text-sm leading-6 text-gray-600">
							<RequiredField /> - Denotes a required field
						</p>
					</div>

					<div className="max-w-2xl grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
						<Field className="sm:col-span-4">
							<Label
								htmlFor="external-id"
								className="block font-medium leading-6 text-gray-900">
								{config?.proctorExtIdLabel}
								<RequiredField />
							</Label>
							<Description as="span" className="text-sm leading-6 text-gray-600">
								Do not put the T in front of your CACTUS number when registering.
								This field should only contain numbers.
							</Description>
							<Input
								type="text"
								name="external-id"
								id="external-id"
								className="block w-full mt-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-primary-600 focus:ring-2 sm:text-sm sm:leading-6"
								required={true}
								maxLength={registrationInfo?.rules.externalIdMaxLength}
								value={externalId}
								onChange={(e) => setExternalId(e.target.value)}
							/>
						</Field>

						<Field className="sm:col-span-3">
							<Label
								htmlFor="first-name"
								className="block font-medium leading-6 text-gray-900">
								First Name
								<RequiredField />
							</Label>
							<Input
								type="text"
								name="first-name"
								id="first-name"
								className="block w-full mt-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-primary-600 focus:ring-2 sm:text-sm sm:leading-6"
								required={true}
								maxLength={registrationInfo?.rules.firstNameMaxLength}
								value={firstName}
								onChange={(e) => setFirstName(e.target.value)}
							/>
						</Field>
						<Field className="sm:col-span-3">
							<Label
								htmlFor="last-name"
								className="block font-medium leading-6 text-gray-900">
								Last Name
								<RequiredField />
							</Label>
							<Input
								type="text"
								name="last-name"
								id="last-name"
								autoComplete="family-name"
								className="block w-full mt-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-primary-600 focus:ring-2 sm:text-sm sm:leading-6"
								required={true}
								maxLength={registrationInfo?.rules.lastNameMaxLength}
								value={lastName}
								onChange={(e) => setLastName(e.target.value)}
							/>
						</Field>

						<Field className="sm:col-span-3">
							<Label
								htmlFor="email"
								className="block font-medium leading-6 text-gray-900">
								Email Address
								<RequiredField />
							</Label>
							<Input
								id="email"
								name="email"
								type="email"
								value={email}
								required
								maxLength={registrationInfo?.rules.emailMaxLength}
								onChange={(e) => setEmail(e.target.value)}
								className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
							/>
						</Field>
						<Field className="sm:col-span-3">
							<Label
								htmlFor="phone"
								className="block font-medium leading-6 text-gray-900">
								Phone Number
							</Label>
							<Input
								id="phone"
								name="phone"
								type="tel"
								value={phone}
								maxLength={registrationInfo?.rules.phoneMaxLength}
								onChange={(e) => setPhone(e.target.value)}
								className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
							/>
						</Field>

						<Field className="sm:col-span-6">
							<Label
								htmlFor="hash"
								className="block font-medium leading-6 text-gray-900">
								Social Security Number Product
								<RequiredField />
							</Label>
							<Description as="p" className="text-sm leading-6 text-88gray-600">
								This number is calculated by multiplying all the non-zero digits of
								your Social Security number.
							</Description>
							<Description as="p" className="text-sm mt-2 leading-6 text-gray-600">
								For example, if your Social Security number is{" "}
								<span className="font-semibold">111-22-0333</span>, you would
								multiply 1 × 1 × 1 × 2 × 2 × 3 × 3 × 3 and enter{" "}
								<span className="font-semibold">108</span> in the field.
							</Description>
							<Input
								type="number"
								name="hash"
								id="hash"
								className="block w-full mt-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-primary-600 focus:ring-2 sm:text-sm sm:leading-6"
								required={true}
								value={hash}
								onChange={(e) => setHash(e.target.value)}
							/>
						</Field>

						<Field className="sm:col-span-2">
							<Label
								htmlFor="month"
								className="block font-medium leading-6 text-gray-900">
								Birthday
								<RequiredField />
							</Label>
							<SelectList
								id="month"
								selectListItems={registrationInfo?.possibleMonths ?? []}
								value={birthMonth}
								className="mt-2 w-full !py-2"
								onChange={handleBirthMonthChange}
							/>
						</Field>
						<Field className="sm:col-span-1">
							<SelectList
								id="birthdate"
								selectListItems={registrationInfo?.possibleDays ?? []}
								value={birthDate}
								className="mt-8 -ml-4 w-full !py-2"
								onChange={handleBirthDateChange}
							/>
						</Field>
						<Field className="sm:col-span-3">
							<Label
								htmlFor="gender"
								className="block font-medium leading-6 text-gray-900">
								Gender
								<RequiredField />
							</Label>
							<SelectList
								id="gender"
								selectListItems={registrationInfo?.possibleGenders ?? []}
								value={gender}
								className="mt-2 w-full !py-2"
								onChange={handleGenderChange}
							/>
						</Field>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
					<div>
						<h2 className="text-2xl leading-7">Account Information</h2>
						<p className="mt-1 text-sm leading-6 text-gray-600">
							<RequiredField /> - Denotes a required field
						</p>
					</div>

					<div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
						<Field className="sm:col-span-4">
							<Label
								htmlFor="username"
								className="block font-medium leading-6 text-gray-900">
								Username
								<RequiredField />
							</Label>
							<Description as="span" className="text-sm leading-6 text-gray-600">
								Enter TCACTUS as your username. For example: T1234
							</Description>
							<Input
								id="username"
								name="username"
								type="text"
								maxLength={registrationInfo?.rules.userNameMaxLength}
								minLength={registrationInfo?.rules.userNameMinLength}
								onChange={(e) => setUsername(e.target.value)}
								value={username ?? ""}
								className="block w-full mt-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
							/>
						</Field>

						<Field className="sm:col-span-4">
							<Label
								htmlFor="password"
								className="block font-medium leading-6 text-gray-900">
								Password
								<RequiredField />
							</Label>
							<Description as="span" className="text-sm leading-6 text-gray-600">
								Your password must be at least{" "}
								{registrationInfo?.rules.nonWriterPasswordMinLength} characters
								long.
							</Description>
							<Password
								id="password"
								name="password"
								minLength={registrationInfo?.rules.nonWriterPasswordMinLength}
								maxLength={registrationInfo?.rules.passwordMaxLength}
								required={true}
								className="block w-full mt-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
								onChange={(e) => setPassword(e.target.value)}
								value={password}
							/>
						</Field>

						<Field className="sm:col-span-4">
							<Label
								htmlFor="confirm-password"
								className="block font-medium leading-6 text-gray-900">
								Confirm Password
								<RequiredField />
							</Label>
							<Password
								id="confirm-password"
								name="confirm-password"
								placeholder="confirm password"
								minLength={registrationInfo?.rules.nonWriterPasswordMinLength}
								maxLength={registrationInfo?.rules.passwordMaxLength}
								required={true}
								className="block w-full mt-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
								onChange={(e) => setConfirmPassword(e.target.value)}
								value={confirmPassword}
							/>
						</Field>
					</div>
				</div>
			</div>

			<div className="my-6 flex items-center justify-end gap-x-6">
				<button
					type="submit"
					disabled={disableSave}
					className="btn btn-primary"
					onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleSave(e)}>
					Save
				</button>
				<button type="button" className="btn btn-naked" onClick={() => navigate("/login")}>
					Cancel
				</button>
			</div>
		</form>
	);
}
